import { Container, Graphics } from 'pixi.js';
import type { World, EntityId } from '@/core/ECS';
import { Drift, Position, Powerup, Renderable, Velocity } from '@/components';
import { rand } from '@/utils/rng';

/**
 * createPowerup — rare drift entity the player absorbs for a big effect.
 *
 * kind='bomb'   — detonates on pickup, killing ~70% of predators on screen
 *                 (AbsorptionSystem handles detonation). Visual: angry
 *                 orange-red pulsing core with radial spikes.
 * kind='shield' — grants short invulnerability on pickup. Visual: cyan
 *                 hexagonal ring with slow rotation.
 *
 * Powerups drift slowly like food; lifetime caps prevent screen clutter.
 */

const BOMB_COLOR = 0xff6a3a;
const BOMB_CORE = 0xffd08a;
const SHIELD_COLOR = 0x4fd1c5;

export function createPowerup(
  world: World,
  parent: Container,
  kind: 'bomb' | 'shield',
  x: number,
  y: number,
): EntityId {
  const e = world.createEntity();

  const root = new Container();
  root.position.set(x, y);
  const glow = new Graphics();
  const body = new Graphics();
  root.addChild(glow, body);
  parent.addChild(root);

  const radius = kind === 'bomb' ? 14 : 13;
  const draw = kind === 'bomb' ? drawBomb : drawShield;
  draw(0, body, glow);

  world.addComponent(e, Position, { x, y });
  world.addComponent(e, Velocity, { vx: 0, vy: 0 });
  world.addComponent(e, Renderable, {
    root, body, glow, baseScale: 1, time: rand() * 10,
    draw: (t, b, g) => draw(t, b, g),
  });
  world.addComponent(e, Powerup, {
    kind,
    radius,
    age: 0,
    lifetime: kind === 'bomb' ? 18 : 14,
  });

  const angle = rand() * Math.PI * 2;
  const speed = 10 + rand() * 6;
  world.addComponent(e, Drift, {
    baseVx: Math.cos(angle) * speed,
    baseVy: Math.sin(angle) * speed,
    wanderSeed: rand() * 1000,
    strength: 6,
  });

  return e;
}

function drawBomb(time: number, body: Graphics, glow?: Graphics): void {
  const pulse = 1 + Math.sin(time * 5) * 0.12;
  const r = 14 * pulse;

  if (glow) {
    glow.clear();
    glow.circle(0, 0, r * 2.2).fill({ color: BOMB_COLOR, alpha: 0.18 });
    glow.circle(0, 0, r * 1.5).fill({ color: BOMB_COLOR, alpha: 0.25 });
  }

  body.clear();
  // radial spikes (warning aura)
  const spikes = 8;
  for (let i = 0; i < spikes; i++) {
    const a = (i / spikes) * Math.PI * 2 + time * 0.8;
    const len = r * 1.8;
    const x1 = Math.cos(a) * r * 0.9;
    const y1 = Math.sin(a) * r * 0.9;
    const x2 = Math.cos(a) * len;
    const y2 = Math.sin(a) * len;
    body.moveTo(x1, y1).lineTo(x2, y2).stroke({ color: BOMB_COLOR, width: 2.2, alpha: 0.8 });
  }
  // outer ring
  body.circle(0, 0, r).fill({ color: BOMB_COLOR, alpha: 0.85 });
  body.circle(0, 0, r).stroke({ color: 0xffffff, width: 1.2, alpha: 0.6 });
  // bright inner core (flickers)
  const flicker = 0.7 + Math.sin(time * 11) * 0.3;
  body.circle(0, 0, r * 0.45).fill({ color: BOMB_CORE, alpha: flicker });
  body.circle(0, 0, r * 0.2).fill({ color: 0xffffff, alpha: 0.9 });
}

function drawShield(time: number, body: Graphics, glow?: Graphics): void {
  const r = 13 + Math.sin(time * 2.5) * 0.8;

  if (glow) {
    glow.clear();
    glow.circle(0, 0, r * 2).fill({ color: SHIELD_COLOR, alpha: 0.18 });
    glow.circle(0, 0, r * 1.4).fill({ color: SHIELD_COLOR, alpha: 0.22 });
  }

  body.clear();
  // rotating hexagon
  const rot = time * 0.6;
  const outer: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + rot;
    outer.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  body.moveTo(outer[0][0], outer[0][1]);
  for (let i = 1; i < 6; i++) body.lineTo(outer[i][0], outer[i][1]);
  body.closePath();
  body.fill({ color: SHIELD_COLOR, alpha: 0.3 });
  body.stroke({ color: SHIELD_COLOR, width: 2, alpha: 0.95 });

  // inner counter-rotating hex
  const rot2 = -time * 0.9;
  const inner: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + rot2;
    inner.push([Math.cos(a) * r * 0.55, Math.sin(a) * r * 0.55]);
  }
  body.moveTo(inner[0][0], inner[0][1]);
  for (let i = 1; i < 6; i++) body.lineTo(inner[i][0], inner[i][1]);
  body.closePath();
  body.stroke({ color: 0xffffff, width: 1.4, alpha: 0.7 });
  body.circle(0, 0, r * 0.2).fill({ color: 0xffffff, alpha: 0.85 });
}
